import React, { useEffect } from 'react';
import { Box, Typography, LinearProgress, Paper, Grid, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import { TaskCompletionStatus } from '../../../Api/AI/aiApi';
import { FileNumberRenderer } from './FileNumber';
import { Icon } from '../../Atoms/Icon';
import { TextSpan } from '../../Atoms/Text';
import { DEFAULT_ADVANCE_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { InvoiceParserJob, retryInvoiceParserJob, updateInvoiceParserJob } from '../../../store/InvoiceParser';

export type ProcessingStatus =
  | 'idle'
  | 'calculating_sha'
  | 'getting_upload_url'
  | 'uploading'
  | 'parsing'
  | 'completed'
  | 'error';

interface StatusDisplayProps {
  invoiceParserJobs: InvoiceParserJob[];
}

const StatusDisplay: React.FC<StatusDisplayProps> = ({ invoiceParserJobs }) => {
  return (
    <Paper
      elevation={0}
      sx={{ backgroundColor: '#fafbf8', px: 4, py: 2, borderRadius: 2, maxHeight: '370px', overflowY: 'auto' }}
      className="bg-card-1">
      <Typography
        variant="body2"
        color="textSecondary"
        gutterBottom>
        In Progress:
      </Typography>
      {invoiceParserJobs.map((job) => (
        <StatusDisplayRow
          key={job.id}
          jobId={job.id as string}
          status={job.status}
          fileName={job.fileName}
          fileNumber={job.fileNumber}
          overallStatus={job.overallStatus}
          updateOverallStatus={(status: number) => updateInvoiceParserJob(job.id as string, { overallStatus: status })}
          taskCompletionStatus={job.taskCompletionStatus}
          createdItems={job.inventoryList?.length || 0}
          error={job.error || undefined}
        />
      ))}
    </Paper>
  );
};

interface StatusDisplayRowProps {
  jobId: string;
  status: ProcessingStatus;
  fileName: string;
  fileNumber?: number;
  overallStatus: number;
  updateOverallStatus: (status: number) => void;
  taskCompletionStatus?: TaskCompletionStatus;
  createdItems: number;
  error?: string;
}

const StatusDisplayRow = ({
  jobId,
  status,
  fileName,
  fileNumber,
  overallStatus,
  updateOverallStatus,
  taskCompletionStatus,
  createdItems,
  error
}: StatusDisplayRowProps) => {
  const [completionStatus, setCompletionStatus] = React.useState<number>(overallStatus);

  const handleUpdateStatus = (status: number) => {
    setCompletionStatus(status);
    updateOverallStatus(status);
  };

  const getStatusPercentage = (status: string, taskStatus?: TaskCompletionStatus) => {
    switch (status) {
      case 'calculating_sha':
        return 1;
      case 'getting_upload_url':
        return 2;
      case 'uploading':
        return 5;
      case 'parsing':
        if (!taskStatus || taskStatus.reading.status === 0) return 6;
        if (taskStatus.reading.status === 1) {
          const { pages, pagesRead } = taskStatus.reading;
          const readingProgress = (pages > 0 ? (pagesRead / pages) * 24 : 0).toFixed(0);
          return 6 + Number(readingProgress);
        }
        if (taskStatus.reading.status === 2 && taskStatus.docClassification === 0) return 31;
        if (taskStatus.reading.status === 2 && taskStatus.docClassification === 1) return 32;
        if (taskStatus.docClassification === 2 && taskStatus.parsing === 0) return 35;
        if (taskStatus.docClassification === 2 && taskStatus.parsing === 1) {
          const { pages } = taskStatus.reading;
          const increment = Number((63 / Math.log(pages * 1000 + 1) / 2).toFixed(0));
          const newStatus = completionStatus + increment;
          return newStatus > 98 ? 98 : newStatus;
        }
        if (taskStatus.parsing === 2) return 99;
        return 99;
      case 'completed':
        return 100;
      case 'error':
        return 100;
      default:
        return 0;
    }
  };

  const getIconForStatus = (status: number) => {
    switch (status) {
      case 1:
        return (
          <CircularProgress
            size={12}
            color="primary"
          />
        );
      case 2:
        return <span className="circle-check-outline-icon" />;
      case 3:
        return <CloseIcon style={{ width: 18, height: 18, color: 'red' }} />;
      default:
        return <span className="empty-circle-icon" />;
    }
  };

  useEffect(() => {
    const percentage = getStatusPercentage(status, taskCompletionStatus);
    handleUpdateStatus(percentage);
  }, [status, taskCompletionStatus]);

  return (
    <Box sx={{ mt: 2, mb: 4, flexDirection: 'row', display: 'flex', alignItems: 'end', width: '100%', gap: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <FileNumberRenderer fileNumber={fileNumber || 0} />
      </Box>
      <Box sx={{ maxWidth: '390px', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        <Typography
          variant="body2"
          gutterBottom>
          {fileName}
        </Typography>
        <Box sx={{ flexDirection: 'row', display: 'flex', gap: 1, alignItems: 'end', width: '100%' }}>
          <Box sx={{ width: '350px', maxWidth: '350px', mr: 2 }}>
            <LinearProgress
              sx={{
                mt: 1,
                mb: 0.5,
                height: 10,
                borderRadius: 5,
                backgroundColor: '#EEF0E9',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: status === 'error' ? '#FF6B6B' : '#A0A7BA',
                  borderRadius: 5
                }
              }}
              variant={'determinate'}
              value={status === 'error' ? 100 : completionStatus}
              color={status === 'error' ? 'error' : 'primary'}
            />
          </Box>
          <Typography
            variant="caption"
            sx={{
              transform: 'translate(-50%, 0)',
              color: 'black',
              fontSize: '12px',
              transition: 'left 0.3s ease-in-out'
            }}>
            {status === 'error' ? '100%' : `${completionStatus}%`}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ flexDirection: 'row', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Grid
          item
          xs={1}
          display="flex"
          justifyContent="center"
          alignItems="center">
          {getIconForStatus(taskCompletionStatus?.reading.status || 0)}
        </Grid>
        <Grid
          item
          xs={11}>
          <Typography variant="body2">Reading</Typography>
        </Grid>
      </Box>
      <Box sx={{ flexDirection: 'row', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Grid
          item
          xs={1}
          display="flex"
          justifyContent="center"
          alignItems="center">
          {getIconForStatus(taskCompletionStatus?.docClassification || 0)}
        </Grid>
        <Grid
          item
          xs={11}>
          <Typography variant="body2">Doc. Classification</Typography>
        </Grid>
      </Box>
      <Box sx={{ flexDirection: 'row', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Grid
          item
          xs={1}
          display="flex"
          justifyContent="center"
          alignItems="center">
          {getIconForStatus(taskCompletionStatus?.parsing || 0)}
        </Grid>
        <Grid
          item
          xs={11}>
          <Typography variant="body2">Parsing</Typography>
        </Grid>
      </Box>
      {error && (
        <div className="p-2 rounded-xl bg-card-1 flex justify-between align-middle items-center gap-2">
          <Tooltip
            title={error}
            arrow>
            <Icon
              type="alert"
              className="p-2 inline-block scale-[150%]"
            />
          </Tooltip>
          <TextSpan
            size="sm14"
            weight="semibold"
            className="mr-6">
            Sorry, an error occurred
          </TextSpan>
          <button
            className={`${DEFAULT_ADVANCE_BUTTON_STYLE} flex justify-between gap-1`}
            onClick={() => retryInvoiceParserJob(jobId)}>
            <Icon
              type="retryWhite"
              className="inline-block scale-[75%]"
            />
            <TextSpan
              size="sm14"
              color="white">
              Retry Upload
            </TextSpan>
          </button>
        </div>
      )}
      {status === 'completed' && createdItems === 0 && !error && (
        <div className="p-2 rounded-xl bg-card-1 flex justify-between align-middle items-center gap-2">
          <Icon
            type="alert"
            className="p-2 inline-block scale-[150%]"
          />
          <TextSpan
            size="sm14"
            weight="semibold"
            className="mr-6">
            No inventory items were identified in this file
          </TextSpan>
        </div>
      )}
    </Box>
  );
};

export default StatusDisplay;
